import React, { useState, useEffect } from "react";
import axios from "axios";

const Step3FinancialInfo = ({ formData, updateFormData, nextStep, prevStep, token }) => {
  const [errors, setErrors] = useState({});
  const [natureOfBusinessOptions, setNatureOfBusinessOptions] = useState([]);

  // Fetch nature of business options (uses business categories endpoint)
  useEffect(() => {
    const fetchNatureOfBusiness = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API}get-categories`, {
          headers: { "x-access-token": token },
        });
        if (response.data.status === 200) {
          // Map category_name to name for consistency
          const categories = (response.data.data || []).map((cat) => ({
            id: cat.id,
            name: cat.category_name,
          }));
          setNatureOfBusinessOptions(categories);
        }
      } catch (error) {
        console.error("Error fetching nature of business:", error);
        // Fallback options if API fails
        setNatureOfBusinessOptions([
          { id: 1, name: "Technology" },
          { id: 2, name: "Healthcare" },
          { id: 3, name: "Real Estate" },
          { id: 4, name: "Finance" },
          { id: 5, name: "Retail" },
          { id: 6, name: "Manufacturing" },
          { id: 7, name: "Food & Beverage" },
          { id: 8, name: "Entertainment" },
          { id: 9, name: "Education" },
          { id: 10, name: "Other" },
        ]);
      }
    };
    fetchNatureOfBusiness();
  }, [token]);

  const handleInputChange = (field, value) => {
    updateFormData({ [field]: value });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleNatureOfBusinessChange = (id) => {
    const currentSelection = formData.nature_of_business || [];
    let newSelection;

    if (currentSelection.includes(id)) {
      newSelection = currentSelection.filter((item) => item !== id);
    } else {
      newSelection = [...currentSelection, id];
    }

    updateFormData({ nature_of_business: newSelection });
  };

  const validateAndNext = () => {
    const newErrors = {};

    if (formData.campaign_type !== "3") {
      if (!formData.target_raise || parseFloat(formData.target_raise) <= 0) {
        newErrors.target_raise = "Target raise amount is required";
      }
    } else if (formData.target_raise && parseFloat(formData.target_raise) <= 0) {
      newErrors.target_raise = "Target raise amount must be positive";
    }

    // Valuation is optional but must be positive if provided
    if (formData.valuation && parseFloat(formData.valuation) < 0) {
      newErrors.valuation = "Valuation must be a positive number";
    }

    // Revenue is optional but must be positive if provided
    if (formData.revenue_last_year && parseFloat(formData.revenue_last_year) < 0) {
      newErrors.revenue_last_year = "Revenue must be a positive number";
    }

    // Year founded validation
    if (formData.year_founded) {
      const year = parseInt(formData.year_founded);
      const currentYear = new Date().getFullYear();
      if (year < 1800 || year > currentYear) {
        newErrors.year_founded = `Year must be between 1800 and ${currentYear}`;
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      nextStep();
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="step-form">
      <div className="form-section">
        <h3 className="section-title">Financial Information</h3>

        <div className="form-row">
          <div className="form-group">
            <label>
              Target Raise Amount ($) {formData.campaign_type !== "3" && <span className="required">*</span>}
            </label>
            <input
              type="number"
              placeholder="e.g., 500000"
              value={formData.target_raise}
              onChange={(e) => handleInputChange("target_raise", e.target.value)}
              min="1"
            />
            {errors.target_raise && <div className="error-message">{errors.target_raise}</div>}
            <div className="help-text">
              {formData.campaign_type === "3"
                ? "Optional - total offering for funds"
                : "Required - total amount to be raised"}
            </div>
          </div>

          <div className="form-group">
            <label>Company Valuation ($)</label>
            <input
              type="number"
              placeholder="e.g., 5000000"
              value={formData.valuation}
              onChange={(e) => handleInputChange("valuation", e.target.value)}
              min="0"
            />
            {errors.valuation && <div className="error-message">{errors.valuation}</div>}
            <div className="help-text">Optional - Current company valuation</div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Revenue Last Year ($)</label>
            <input
              type="number"
              placeholder="e.g., 1000000"
              value={formData.revenue_last_year}
              onChange={(e) => handleInputChange("revenue_last_year", e.target.value)}
              min="0"
            />
            {errors.revenue_last_year && (
              <div className="error-message">{errors.revenue_last_year}</div>
            )}
            <div className="help-text">Optional - Annual revenue from previous year</div>
          </div>

          <div className="form-group">
            <label>Number of Employees</label>
            <input
              type="number"
              placeholder="e.g., 25"
              value={formData.number_of_employees}
              onChange={(e) => handleInputChange("number_of_employees", e.target.value)}
              min="0"
            />
            <div className="help-text">Optional - Current employee count</div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Year Founded</label>
            <input
              type="number"
              placeholder="e.g., 2020"
              value={formData.year_founded}
              onChange={(e) => handleInputChange("year_founded", e.target.value)}
              min="1800"
              max={currentYear}
            />
            {errors.year_founded && <div className="error-message">{errors.year_founded}</div>}
            <div className="help-text">Optional - Year the company was founded</div>
          </div>
        </div>

        {/* Nature of Business */}
        <div className="form-group">
          <label>Nature of Business</label>
          <div className="help-text" style={{ marginBottom: "10px" }}>
            Select all that apply
          </div>
          <div className="checkbox-group">
            {natureOfBusinessOptions.map((option) => (
              <div key={option.id} className="checkbox-item">
                <input
                  type="checkbox"
                  id={`nob-${option.id}`}
                  checked={(formData.nature_of_business || []).includes(option.id)}
                  onChange={() => handleNatureOfBusinessChange(option.id)}
                />
                <label htmlFor={`nob-${option.id}`}>{option.name}</label>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3 className="section-title">Banking Information (Optional)</h3>
        <p style={{ color: "#666", marginBottom: "20px" }}>
          Banking details for fund disbursement. Can be added later if not available.
        </p>

        <div className="form-row">
          <div className="form-group">
            <label>Bank Name</label>
            <input
              type="text"
              placeholder="e.g., Chase Bank"
              value={formData.bank_name}
              onChange={(e) => handleInputChange("bank_name", e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Account Holder Name</label>
            <input
              type="text"
              placeholder="Name on the account"
              value={formData.account_name}
              onChange={(e) => handleInputChange("account_name", e.target.value)}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Account Number</label>
            <input
              type="text"
              placeholder="Enter account number"
              value={formData.account_number}
              onChange={(e) => handleInputChange("account_number", e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Routing Number</label>
            <input
              type="text"
              placeholder="Enter routing number"
              value={formData.routing_number}
              onChange={(e) => handleInputChange("routing_number", e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="step-buttons">
        <button className="btn btn-secondary" onClick={prevStep}>
          Back
        </button>
        <button className="btn btn-primary" onClick={validateAndNext}>
          Next: Files & Media
        </button>
      </div>
    </div>
  );
};

export default Step3FinancialInfo;
